<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useUserStore } from '@/store/user';
import { useUploadsStore } from '@/store/uploads';
import { BTooltip } from 'bootstrap-vue-next';
import { useRoute } from 'vue-router';

const emits = defineEmits(['toggle']);

const userStore = useUserStore();
const uploadsStore = useUploadsStore();

const sidebarToggled = ref(false);
const subMenuOpen = ref({
  analytics: true,
  admin: true,
  devTools: true,
  settings: true,
});

const route = useRoute();

// Track window width
const screenWidth = ref<number>(window.innerWidth);

function updateWidth() {
  screenWidth.value = window.innerWidth;
}

// Listen for resize events
onMounted(() => {
  window.addEventListener('resize', updateWidth);
});
onUnmounted(() => {
  window.removeEventListener('resize', updateWidth);
});

// If screen is > 700px, "default visible" => hidden if sidebarToggled is true
// If screen is <= 700px, "default hidden" => hidden if sidebarToggled is false
const isMobile = computed<boolean>(() => {
  return screenWidth.value <= 700;
});

const isSidebarHidden = computed<boolean>(() => {
  if (!isMobile.value) {
    // Desktop: default visible => hidden if sidebarToggled is true
    return sidebarToggled.value === true;
  } else {
    // Mobile: default hidden => hidden if sidebarToggled is false
    return sidebarToggled.value === false;
  }
});

// close the sidebar on every route change (mobile only)
watch(
  () => route.fullPath, // runs after any navigation
  () => {
    if (isMobile.value && !isSidebarHidden.value) {
      toggleSidebar();
    }
  }
);

// keep visual state when the breakpoint is crossed ---------------
watch(isMobile, (nowMobile, wasMobile) => {
  // sidebar was hidden on large screen -> going to mobile
  if (nowMobile && !wasMobile && sidebarToggled.value) {
    toggleSidebar(); // toggle back to keep hidden sidebar on mobile
  }
});

// --- sidebar handlers ---
function toggleSidebar() {
  sidebarToggled.value = !sidebarToggled.value;
  emits('toggle', sidebarToggled.value);
}

function toggleSubMenu(menu: 'analytics' | 'settings' | 'admin' | 'devTools') {
  subMenuOpen.value[menu] = !subMenuOpen.value[menu];
}

function logout() {
  userStore.logout();
}
</script>

<template>
  <nav id="sidebar">
    <div
      id="sideToggle"
      :class="{ 'sidebar-visibility-toggled': sidebarToggled }"
    >
      <button type="button" id="btnSidebarToggle" @click="toggleSidebar">
        <font-awesome-icon v-if="isSidebarHidden" :icon="['fas', 'bars']" />
        <font-awesome-icon v-if="!isSidebarHidden" :icon="['fas', 'bars']" />
        <span class="sr-only">Toggle Menu</span>
      </button>
    </div>
    <div
      id="sideColumn"
      :class="{ 'sidebar-visibility-toggled': sidebarToggled }"
      class="p-4"
    >
      <!-- Side Bar Header-->
      <div class="header">
        <!-- Go to Dashboard when clicked on the logo -->
        <router-link
          :to="{ name: 'Dashboard' }"
          style="text-decoration: none"
          class="p-0 m-0"
        >
          <img
            src="@/assets/images/logo-light.svg"
            class="logo"
            alt="Sampling Human"
        /></router-link>
        <!-- Show user email - link to Personal Information View -->

        <router-link :to="{ name: 'PersonalInformation' }" class="user">
          <small>
            <font-awesome-icon :icon="['fa', 'user']" />
            {{ userStore.email }}
          </small>
        </router-link>
        <hr />
      </div>
      <!-- Side Bar Content -->
      <div class="content">
        <ul class="list-unstyled">
          <li>
            <router-link :to="{ name: 'Dashboard' }"
              ><font-awesome-icon :icon="['fas', 'home']" />
              Dashboard</router-link
            >
          </li>
          <!-- ANALYTICS -->
          <li>
            <a
              href="#"
              @click.prevent="toggleSubMenu('analytics')"
              class="dropdown-toggle"
            >
              <font-awesome-icon :icon="['fas', 'line-chart']" />
              Analytics
            </a>
            <ul v-show="subMenuOpen.analytics" class="list-unstyled">
              <li>
                <router-link :to="{ name: 'Projects' }"
                  ><font-awesome-icon :icon="['fas', 'list']" />
                  Projects</router-link
                >
              </li>
              <li>
                <router-link :to="{ name: 'DataManager' }"
                  ><font-awesome-icon :icon="['fas', 'database']" /> Data
                  Manager</router-link
                >
              </li>
              <li>
                <router-link :to="{ name: 'UploadManager' }">
                  <font-awesome-icon :icon="['fas', 'upload']" />
                  Upload Manager
                  <!-- show beating exclamation mark if there are uploads with stale status -->
                  <span v-if="uploadsStore.hasStaleUploads">
                    <font-awesome-icon
                      id="warning-stale-uploads"
                      :icon="['fas', 'triangle-exclamation']"
                      class="fa-beat"
                      style="color: #ffc107"
                    />
                    <b-tooltip target="warning-stale-uploads">
                      Some uploads seem to be in a stale state. See the upload
                      manager for details. Ignore this if your uploads are
                      actively managed by another instance of the app running in
                      another tab or on another device.
                    </b-tooltip>
                  </span>
                </router-link>
              </li>
            </ul>
          </li>
          <!-- ADMIN -->
          <li v-if="userStore.admin">
            <a
              href="#"
              @click.prevent="toggleSubMenu('admin')"
              class="dropdown-toggle"
            >
              <font-awesome-icon :icon="['fas', 'layer-group']" /> Admin
            </a>
            <ul v-show="subMenuOpen.admin" class="list-unstyled">
              <li>
                <router-link :to="{ name: 'AdminCodesList' }"
                  ><font-awesome-icon :icon="['fas', 'ticket']" /> Kits and
                  Coupons</router-link
                >
              </li>
              <li>
                <router-link :to="{ name: 'AdminOrganizationList' }"
                  ><font-awesome-icon :icon="['fas', 'users']" />
                  Organizations</router-link
                >
              </li>
              <li>
                <router-link :to="{ name: 'AdminUserList' }"
                  ><font-awesome-icon :icon="['fas', 'user']" />
                  Users</router-link
                >
              </li>
              <li>
                <router-link :to="{ name: 'AdminInvitesList' }"
                  ><font-awesome-icon :icon="['fas', 'envelope']" />
                  Invites</router-link
                >
              </li>
              <li>
                <router-link :to="{ name: 'SystemDataManager' }"
                  ><font-awesome-icon :icon="['fas', 'database']" /> System
                  Data</router-link
                >
              </li>
            </ul>
          </li>
          <!-- DEV TOOLS -->
          <li v-if="userStore.developer">
            <a
              href="#"
              @click.prevent="toggleSubMenu('devTools')"
              class="dropdown-toggle"
            >
              <font-awesome-icon :icon="['fas', 'code']" /> Dev Tools
            </a>
            <ul v-show="subMenuOpen.devTools" class="list-unstyled">
              <li>
                <router-link :to="{ name: 'AdminComputeTasks' }"
                  ><font-awesome-icon :icon="['fas', 'list-check']" /> Compute
                  Tasks</router-link
                >
              </li>
              <li v-if="userStore.developer">
                <router-link :to="{ name: 'AdminStats' }"
                  ><font-awesome-icon :icon="['fas', 'chart-simple']" />
                  Stats</router-link
                >
              </li>
              <li>
                <router-link :to="{ name: 'AdminLogs' }">
                  <font-awesome-icon :icon="['fas', 'file-lines']" />
                  Logs
                </router-link>
              </li>
            </ul>
          </li>
          <!-- SETTINGS -->
          <li>
            <a
              href="#"
              @click.prevent="toggleSubMenu('settings')"
              class="dropdown-toggle"
            >
              <font-awesome-icon :icon="['fas', 'gears']" /> Settings
            </a>
            <ul v-show="subMenuOpen.settings" class="list-unstyled">
              <li>
                <router-link :to="{ name: 'PersonalInformation' }"
                  ><font-awesome-icon :icon="['fas', 'user']" /> Personal
                  information</router-link
                >
              </li>
              <li>
                <router-link :to="{ name: 'OrganizationSettings' }"
                  ><font-awesome-icon :icon="['fas', 'users']" /> Organization
                  settings</router-link
                >
              </li>
            </ul>
          </li>
          <li>
            <a href="#" @click.prevent="logout">
              <font-awesome-icon :icon="['fas', 'sign-out']" /> Sign Out
            </a>
          </li>
        </ul>
      </div>
      <!-- Side Bar Footer -->
      <div class="footer">
        <p>
          <!-- link to website in new tab -->
          <a
            href="https://www.samplinghuman.com"
            target="_blank"
            class="p-0 m-0"
          >
            &#169; Sampling Human, Inc.
          </a>
        </p>
      </div>
    </div>
  </nav>
</template>

<style scoped>
#sideToggle {
  position: fixed;
  top: 25px;
  left: 275px;
  transition: all 0.3s;
  z-index: 999;
}
#sideToggle.sidebar-visibility-toggled {
  left: 25px;
}
#sideToggle button {
  text-align: center;
  vertical-align: middle;
  width: 50px;
  height: 50px;
  font-size: 15px;
  border: 1px solid #fafafa;
  background-color: rgba(255, 255, 255, 1);
  color: darkslategray;
  border-radius: 60px;
  box-shadow: 0 0 2px #888;
}
#sideToggle button:hover {
  cursor: pointer;
}
#sideToggle i.show {
  display: none;
}
#sideToggle i.hide {
  display: inline-block;
}
#sideToggle.sidebar-visibility-toggled i.show {
  display: inline-block;
}
#sideToggle.sidebar-visibility-toggled i.hide {
  display: none;
}

/* SIDE BAR */
#sideColumn {
  position: fixed;
  top: 0;
  left: 0;
  width: 300px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  transition: all 0.3s;
  z-index: 998;
  color: rgba(255, 255, 255, 0.75) !important;
}
#sideColumn.sidebar-visibility-toggled {
  width: 300px;
  margin-left: -300px;
}
/* Side Bar Footer */
#sideColumn div.footer {
  margin-top: auto; /* push the footer to the bottom of the flex container */
  flex-shrink: 0; /* ensure the footer doesn't shrink if content is large */
}
#sideColumn div.footer p {
  font-size: 0.75rem;
  font-weight: 300;
}
/* Contained Elements */
#sideColumn hr {
  border: 0.5px solid rgba(255, 255, 255, 1) !important;
  margin: 0;
  padding: 0;
}
#sideColumn img.logo {
  width: 150px;
  height: auto;
  margin: 1rem;
}
#sideColumn a.user {
  font-size: 1rem;
  font-weight: 250;
  padding: 0;
  margin: 0 1rem 1rem 1rem;
  color: rgba(255, 255, 255, 0.8) !important;
}
/* SIDE BAR MENU */
#sideColumn ul ul {
  margin-left: 2rem;
}
#sideColumn .dropdown-toggle:after {
  display: inline-block;
  position: absolute;
  right: 0;
  margin-right: 2rem;
  margin-top: 1rem;
}
/* SIDE BAR LINKS */
#sideColumn a {
  display: block;
  color: rgba(255, 255, 255, 0.6) !important;
  padding: 1rem;
  text-decoration: none !important;
}
#sideColumn ul a {
  font-size: 1rem;
  font-weight: 400;
  border-bottom: 0.5px solid rgba(255, 255, 255, 0.1) !important;
}
#sideColumn ul ul a {
  font-size: 0.75rem;
  font-weight: 350;
  border-bottom: 0.5px solid rgba(255, 255, 255, 0.1) !important;
}
#sideColumn a.active {
  color: white !important;
}
#sideColumn a:hover:not(.active) {
  color: white !important;
}

/* RESPONSIVE */
@media screen and (max-width: 700px) {
  /* hidden sidebar by default */
  #sideToggle {
    left: 25px;
    right: auto;
  }
  #sideColumn {
    width: 100vw;
    margin-left: -100vw;
  }
  #sideToggle i.show {
    display: inline-block;
  }
  #sideToggle i.hide {
    display: none;
  }
  /* shown on toggle */
  #sideToggle.sidebar-visibility-toggled {
    left: calc(100vw - 75px);
  }
  #sideColumn.sidebar-visibility-toggled {
    margin-left: 0;
    width: 100vw;
    text-align: center;
    float: none;
  }
  #sideToggle.sidebar-visibility-toggled i.show {
    display: none;
  }
  #sideToggle.sidebar-visibility-toggled i.hide {
    display: inline-block;
  }
}
</style>
